<template>
  <ion-page data-testid="student-page">
    <ion-header>
      <ion-toolbar>
        <ion-title>
          {{ student?.name }}

          <span v-if="student?.id?.startsWith('offline_')">
            <ion-icon :icon="cloudOfflineOutline" />
          </span>
          <span v-if="studentData.tags?.includes('vip')">
            &nbsp;<ion-icon :icon="heart" color="danger" data-testid="vip-student-icon" />
          </span>
        </ion-title>
        <ion-buttons slot="start">
          <ion-back-button :default-href="{name: 'students'}" />
        </ion-buttons>
        <ion-buttons v-if="!isOfflineStudent" slot="end">
          <ion-button
            color="success" :router-link="{ name: 'chat', params: { chatId: [currentUser.uid, student?.id].sort().join('---') }}"
            data-testid="chat-button"
          >
            <ion-icon slot="icon-only" :icon="chatbubblesOutline" />
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content color="light" class="ion-padding-top">
      <ion-spinner v-if="studentPending" />
      <error-message
        v-if="!student && !studentPending"
        title="No Student Found"
        :content="studentId.startsWith('offline_') ? 'Offline student was deleted' : 'Please check the student ID and try again'"
      />
      <template v-if="student">
        <ion-grid>
          <ion-row class="ion-align-items-center ion-justify-content-center">
            <ion-col class="ion-align-items-center" size="auto">
              <user-avatar :url="student.photoURL" :size="100" :name="student.name" />
            </ion-col>
          </ion-row>
          <ion-row class="ion-text-center">
            <ion-col>
              <p v-if="isOfflineStudent">
                <strong>Email</strong>:
                <a v-if="student.email" :href="`mailto:${student.email}`">{{ student.email }}</a>
                <ion-note v-else>unknown</ion-note>
              </p>
              <p v-if="isOfflineStudent">
                <strong>Phone</strong>:
                <span v-if="student.phone">{{ student.phone }}</span>
                <ion-note v-else>unknown</ion-note>
              </p>
              <p v-if="studentData.homeClub">
                <strong>Home Club:</strong> {{ studentData.homeClub.name }}
              </p>
              <p v-if="studentData.notes">
                <strong>Notes:</strong> "{{ studentData.notes }}"
              </p>
            </ion-col>
          </ion-row>
        </ion-grid>
        <purchased-packages-block :display-book-button="false" :filter-by-user="student" />
        <lessons-block
          :filter-by-user="student" :display-state-description="false" :display-empty-state="false"
          :display-title="true"
        />
        <div style="margin-bottom: 3em" />
      </template>
    </ion-content>
    <ion-footer>
      <ion-toolbar v-if="student">
        <ion-grid>
          <ion-row>
            <ion-col size="auto">
              <ion-button
                expand="block" fill="clear"
                color="medium" :router-link="{
                  name: 'editOfflineStudent', params: {studentId: student?.id}
                }"
                data-testid="edit-student-button"
              >
                <ion-icon slot="start" :icon="createOutline" />
                Edit
              </ion-button>
            </ion-col>
            <ion-col>
              <ion-button
                fill="outline" expand="block" color="medium"
                :router-link="{
                  name: 'createPackagePage',
                  params: {studentId: student?.id}
                }" data-testid="add-package-button"
              >
                <ion-icon slot="start" :icon="duplicateOutline" />
                Package
              </ion-button>
            </ion-col>
            <ion-col>
              <ion-button
                expand="block" color="success" fill="outline"
                data-testid="book-lesson-button" :router-link="{
                  name: 'createLessonPage',
                  params: { studentId: student?.id }
                }"
              >
                <ion-icon slot="start" :icon="calendarOutline" />
              &nbsp;
                <span v-if="isOfflineStudent">Book</span>
                <span v-else>Invite</span>
              </ion-button>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-toolbar>
    </ion-footer>
  </ion-page>
</template>

<script setup>
import { IonBackButton, IonButton, IonButtons, IonCol, IonContent,
  IonFooter, IonGrid, IonHeader, IonIcon, IonNote, IonPage, IonRow,
  IonSpinner, IonTitle, IonToolbar,
} from '@ionic/vue'
import { calendarOutline, chatbubblesOutline, cloudOfflineOutline, createOutline, duplicateOutline, heart } from 'ionicons/icons'
import { computed, watchEffect } from 'vue'
import { useCurrentUser } from 'vuefire'
import { useTitle } from '@vueuse/core'

import ErrorMessage from '@/3_widgets/ErrorMessage.vue'
import LessonsBlock from '@/shared/components/LessonsBlock.vue'
import PurchasedPackagesBlock from '@/3_widgets/packages/PurchasedPackagesBlock.vue'
import UserAvatar from '@/3_widgets/UserAvatar.vue'
import { useUserStore } from '@/stores/user'

const currentUser = useCurrentUser()
const userStore = useUserStore()

const props = defineProps({
  studentId: { type: String, required: true },
})

const isOfflineStudent = computed(() => props.studentId.startsWith('offline_'))

const { data: student, pending: studentPending } = userStore.instructor.fn.getStudent(props.studentId)

const { data: overrideStudentData } = userStore.instructor.fn.getOfflineStudent(props.studentId)

const studentData = computed(() => {
  return {
    notes: overrideStudentData.value?.notes || student.value?.notes,
    tags: overrideStudentData.value?.tags || student.value?.tags,
    homeClub: student.value?.homeClub,
  }
})

watchEffect(() => {
  useTitle(student.value?.name || '')
})
</script>
